import { useState } from 'react';
import { ChevronDown, Quote, Star, CheckCircle2, ArrowRight, Zap } from 'lucide-react';
import { PhosphorIcon } from '../../IconPicker';

interface AboutBlockProps {
  settings: { title: string; text: string; imageUrl?: string };
  primaryColor: string;
}

export function AboutBlock({ settings, primaryColor }: AboutBlockProps) {
  return (
    <section className="py-20 md:py-28 px-4">
      <div className="max-w-6xl mx-auto">
        <div className={`flex flex-col ${settings.imageUrl ? 'lg:flex-row' : ''} items-center gap-12 lg:gap-20`}>
          {settings.imageUrl && (
            <div className="flex-shrink-0 lg:flex-1">
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-15 blur-2xl"
                  style={{ backgroundColor: primaryColor }}
                />
                <img
                  src={settings.imageUrl}
                  alt=""
                  className="relative w-full max-w-md rounded-3xl object-cover shadow-xl"
                />
              </div>
            </div>
          )}
          <div className={`flex-1 ${!settings.imageUrl ? 'text-center max-w-3xl mx-auto' : ''}`}>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
            >
              <span>Über mich</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {settings.title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-wrap">{settings.text}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

interface BenefitsBlockProps {
  settings: { title: string; items: { icon: string; title: string; text: string }[] };
  primaryColor: string;
  accentColor: string;
}

export function BenefitsBlock({ settings, primaryColor }: BenefitsBlockProps) {
  return (
    <section className="py-20 md:py-28 px-4 bg-gradient-to-b from-gray-50/50 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
          >
            <Zap className="w-4 h-4" />
            <span>Vorteile</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{settings.title}</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {settings.items.map((item, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-3xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-gray-100"
            >
              <div
                className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `linear-gradient(135deg, ${primaryColor}05 0%, transparent 100%)` }}
              />
              <div className="relative">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}
                >
                  <PhosphorIcon name={item.icon} size={32} weight="regular" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface StoryBlockProps {
  settings: { title: string; text: string; imageUrl?: string };
  primaryColor: string;
}

export function StoryBlock({ settings, primaryColor }: StoryBlockProps) {
  return (
    <section className="py-20 md:py-28 px-4">
      <div className="max-w-6xl mx-auto">
        <div className={`flex flex-col ${settings.imageUrl ? 'lg:flex-row-reverse' : ''} items-center gap-12 lg:gap-20`}>
          {settings.imageUrl && (
            <div className="flex-shrink-0 lg:flex-1">
              <div className="relative">
                <div
                  className="absolute top-4 left-4 right-4 bottom-4 rounded-3xl"
                  style={{ backgroundColor: `${primaryColor}15` }}
                />
                <img
                  src={settings.imageUrl}
                  alt=""
                  className="relative w-full max-w-lg rounded-3xl object-cover shadow-lg"
                />
              </div>
            </div>
          )}
          <div className={`flex-1 ${!settings.imageUrl ? 'text-center max-w-3xl mx-auto' : ''}`}>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
            >
              <span>Meine Geschichte</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 leading-tight">{settings.title}</h2>
            <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-wrap">{settings.text}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

interface ImageTextBlockProps {
  settings: { title: string; text: string; imageUrl: string; layout: string };
  primaryColor: string;
}

export function ImageTextBlock({ settings, primaryColor }: ImageTextBlockProps) {
  const isImageLeft = settings.layout === 'image-left';

  return (
    <section className="py-20 md:py-28 px-4">
      <div className="max-w-6xl mx-auto">
        <div className={`flex flex-col ${isImageLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-20`}>
          {settings.imageUrl && (
            <div className="flex-1 w-full">
              <div className="relative group">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-2xl"
                  style={{ backgroundColor: primaryColor }}
                />
                <img
                  src={settings.imageUrl}
                  alt=""
                  className="relative w-full rounded-3xl object-cover shadow-lg transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </div>
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">{settings.title}</h2>
            <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-wrap">{settings.text}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

interface TestimonialsBlockProps {
  settings: { title: string; testimonials: { name: string; text: string; imageUrl?: string }[] };
  primaryColor: string;
  accentColor: string;
}

export function TestimonialsBlock({ settings, primaryColor }: TestimonialsBlockProps) {
  if (settings.testimonials.length === 0) return null;

  return (
    <section className="py-20 md:py-28 px-4 bg-gradient-to-b from-gray-50/80 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
          >
            <Star className="w-4 h-4" />
            <span>Kundenstimmen</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{settings.title}</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {settings.testimonials.map((t, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <Quote
                className="absolute top-6 right-6 w-10 h-10 opacity-10 transition-opacity group-hover:opacity-20"
                style={{ color: primaryColor }}
              />
              <div className="flex items-center gap-4 mb-6">
                {t.imageUrl ? (
                  <img
                    src={t.imageUrl}
                    alt=""
                    className="w-14 h-14 rounded-full object-cover ring-4 ring-gray-50"
                  />
                ) : (
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {t.name.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900">{t.name}</p>
                  <div className="flex items-center gap-0.5 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed relative z-10">"{t.text}"</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface CTABlockProps {
  settings: { title: string; buttonLabel: string };
  primaryColor: string;
  onScrollToForm?: () => void;
}

export function CTABlock({ settings, primaryColor, onScrollToForm }: CTABlockProps) {
  return (
    <section className="py-20 md:py-28 px-4">
      <div className="max-w-4xl mx-auto">
        <div
          className="relative rounded-3xl p-12 md:p-16 text-center overflow-hidden"
          style={{ backgroundColor: primaryColor }}
        >
          <div className="absolute top-0 left-0 w-64 h-64 rounded-full opacity-20 blur-3xl bg-white -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl bg-white translate-x-1/3 translate-y-1/3" />

          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">{settings.title}</h2>
            {settings.buttonLabel && (
              <button
                onClick={onScrollToForm}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-white rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                style={{ color: primaryColor }}
              >
                {settings.buttonLabel}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

interface FAQBlockProps {
  settings: { title: string; items: { question: string; answer: string }[] };
  primaryColor: string;
}

export function FAQBlock({ settings, primaryColor }: FAQBlockProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (settings.items.length === 0) return null;

  return (
    <section className="py-20 md:py-28 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
          >
            <span>FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{settings.title}</h2>
        </div>

        <div className="space-y-4">
          {settings.items.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isOpen ? 'shadow-lg border-gray-200' : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                    style={{ backgroundColor: isOpen ? primaryColor : `${primaryColor}10` }}
                  >
                    <ChevronDown
                      className="w-5 h-5 transition-colors"
                      style={{ color: isOpen ? 'white' : primaryColor }}
                    />
                  </div>
                </button>
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed">{item.answer}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

interface TextBlockProps {
  settings: { title: string; text: string };
}

export function TextBlock({ settings }: TextBlockProps) {
  return (
    <section className="py-16 md:py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {settings.title && (
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{settings.title}</h2>
        )}
        <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-wrap">{settings.text}</p>
      </div>
    </section>
  );
}

interface ImageBlockProps {
  settings: { imageUrl: string; caption?: string };
}

export function ImageBlock({ settings }: ImageBlockProps) {
  if (!settings.imageUrl) return null;

  return (
    <section className="py-16 md:py-20 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="relative group">
          <img
            src={settings.imageUrl}
            alt=""
            className="w-full rounded-3xl shadow-lg transition-transform duration-500 group-hover:scale-[1.01]"
          />
        </div>
        {settings.caption && (
          <p className="text-center text-gray-500 mt-4 text-sm">{settings.caption}</p>
        )}
      </div>
    </section>
  );
}

interface VideoBlockProps {
  settings: { videoUrl: string; title?: string };
}

export function VideoBlock({ settings }: VideoBlockProps) {
  const getEmbedUrl = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const embedUrl = getEmbedUrl(settings.videoUrl);
  if (!embedUrl) return null;

  return (
    <section className="py-16 md:py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {settings.title && (
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">{settings.title}</h2>
        )}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          <div className="aspect-video">
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

interface StepsBlockProps {
  settings: { title: string; steps: { number: number; title: string; text: string }[] };
  primaryColor: string;
}

export function StepsBlock({ settings, primaryColor }: StepsBlockProps) {
  if (settings.steps.length === 0) return null;

  return (
    <section className="py-20 md:py-28 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>So funktioniert es</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{settings.title}</h2>
        </div>

        <div className="relative">
          <div
            className="absolute left-8 top-8 bottom-8 w-0.5 hidden md:block"
            style={{ backgroundColor: `${primaryColor}20` }}
          />

          <div className="space-y-8">
            {settings.steps.map((step, index) => (
              <div key={index} className="flex gap-6 md:gap-8 group">
                <div className="relative z-10">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg transition-transform duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: primaryColor,
                      boxShadow: `0 10px 30px -10px ${primaryColor}60`,
                    }}
                  >
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1 pt-3">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface WhatsAppBlockProps {
  settings: { phoneNumber: string; message: string; buttonLabel: string };
  primaryColor: string;
}

export function WhatsAppBlock({ settings }: WhatsAppBlockProps) {
  const handleClick = () => {
    const phone = settings.phoneNumber.replace(/\D/g, '');
    const message = encodeURIComponent(settings.message);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  return (
    <section className="py-16 px-4">
      <div className="max-w-md mx-auto text-center">
        <button
          onClick={handleClick}
          className="group inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
          style={{ boxShadow: '0 10px 40px -10px rgba(37, 211, 102, 0.5)' }}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          {settings.buttonLabel || 'WhatsApp Nachricht senden'}
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </section>
  );
}

interface CustomHTMLBlockProps {
  settings: { html: string };
}

export function CustomHTMLBlock({ settings }: CustomHTMLBlockProps) {
  const sanitizedHtml = settings.html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '');

  return (
    <section className="py-12 px-4">
      <div className="max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    </section>
  );
}
